export const formatMagensaExpiration = (expiration: string): string => {
  if (!expiration || expiration.length !== 4) return ''
  return `20${expiration}`
}

/**
 * Detects the card network from a BIN (up to first 6 digits).
 * Returns a string matching the icon filenames in /public/Payment/.
 *
 * NOTE: This is only used for the Magensa payment flow. Fortis has its own
 * network values that come directly from the Fortis API and align with the
 * Fortis backend and POS — do not use this function for Fortis payments.
 */
export const getNetworkFromBin = (bin: string): string => {
  const clean = bin.replace(/\D/g, '')

  const prefixAsInt = (len: number): number | null => {
    const slice = clean.slice(0, len)
    return slice.length === len ? parseInt(slice, 10) : null
  }

  const p2 = prefixAsInt(2)
  const p3 = prefixAsInt(3)
  const p4 = prefixAsInt(4)
  const p6 = prefixAsInt(6)

  const eq = (prefix: number | null, value: number): boolean => prefix === value
  const inRange = (prefix: number | null, lo: number, hi: number): boolean => prefix !== null && prefix >= lo && prefix <= hi

  // Discover
  if (eq(p4, 6011) || inRange(p6, 622126, 622925) || inRange(p3, 644, 649) || eq(p2, 65)) {
    return 'discover'
  }

  // Mastercard (classic 51–55 + 2-series 2221–2720)
  if (inRange(p4, 2221, 2720) || inRange(p2, 51, 55)) {
    return 'mastercard'
  }

  // American Express
  if (eq(p2, 34) || eq(p2, 37)) {
    return 'amex'
  }

  // JCB
  if (inRange(p4, 3528, 3589)) {
    return 'jcb'
  }

  // Diners Club
  if (inRange(p3, 300, 305) || eq(p4, 3095) || eq(p2, 36) || inRange(p2, 38, 39)) {
    return 'dinersclub'
  }

  // Maestro
  if (eq(p2, 50) || inRange(p2, 56, 58) || eq(p2, 67)) {
    return 'maestro'
  }

  // Visa (kept late to avoid swallowing other 4x ranges)
  if (inRange(p2, 40, 49)) {
    return 'visa'
  }

  // Fallback: broader 2-digit check for readers that only expose the first 2 digits
  if (p2 !== null) {
    if (p2 >= 40 && p2 <= 49) return 'visa'
    if (p2 === 34 || p2 === 37) return 'amex'
    if ((p2 >= 51 && p2 <= 55) || p2 === 22 || p2 === 27) return 'mastercard'
    if (p2 === 60 || p2 === 64 || p2 === 65) return 'discover'
  }

  return 'unknown'
}

/**
 * Maps a card network string to an SVG icon filename in /public/Payment/.
 * Supports both Magensa network names (discover, mastercard, amex) and
 * Fortis network names (disc, mc, AmEx) so both payment flows share the
 * same icons without conflicting naming conventions.
 */
const CARD_ICON_MAP: Record<string, string> = {
  // Visa
  visa: 'visa',
  // Mastercard — Magensa and Fortis names
  mastercard: 'mc',
  mc: 'mc',
  // Discover — Magensa and Fortis names
  discover: 'disc',
  disc: 'disc',
  // Amex — Magensa and Fortis names
  amex: 'AmEx',
  AmEx: 'AmEx',
}

export const getCardIcon = (network: string | undefined): string => CARD_ICON_MAP[network ?? ''] ?? 'unknown'
